import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Eye, EyeOff, Home, AlertCircle, Check, X, Globe, ExternalLink } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";

// Valid US states
const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'PR', name: 'Puerto Rico' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'Washington DC' }
];

// Forwarding service recommendations by region
const FORWARDING_SERVICES = {
  asia: {
    label: 'Asia (Korea, Japan, China, etc.)',
    services: [
      { name: 'Post-Go', url: 'https://www.post-go.co.kr', note: 'Recommended for Korea' },
      { name: 'Shipito', url: 'https://www.shipito.com', note: 'Worldwide shipping' },
      { name: 'MyUS', url: 'https://www.myus.com', note: 'Trusted worldwide' },
    ]
  },
  europe: {
    label: 'Europe',
    services: [
      { name: 'Shipito', url: 'https://www.shipito.com', note: 'Worldwide shipping' },
      { name: 'MyUS', url: 'https://www.myus.com', note: 'One of the largest' },
      { name: 'Planet Express', url: 'https://www.planetexpress.com', note: 'Budget-friendly' },
    ]
  },
  middleEast: {
    label: 'Middle East',
    services: [
      { name: 'MyUS', url: 'https://www.myus.com', note: 'Worldwide shipping' },
      { name: 'Shipito', url: 'https://www.shipito.com', note: 'Multiple warehouses' },
      { name: 'Stackry', url: 'https://www.stackry.com', note: 'Good consolidation' },
    ]
  },
  other: {
    label: 'Other Regions',
    services: [
      { name: 'Shipito', url: 'https://www.shipito.com', note: 'Worldwide shipping' },
      { name: 'MyUS', url: 'https://www.myus.com', note: 'Trusted worldwide' },
      { name: 'Stackry', url: 'https://www.stackry.com', note: 'Good consolidation' },
    ]
  }
};

// Comprehensive list of countries supported by forwarding services AND PayPal G&S
// EXCLUDED: OFAC sanctioned (Cuba, Iran, North Korea, Syria, Russia, Venezuela, Crimea)
// EXCLUDED: PayPal restricted/banned (Pakistan, Bangladesh, Myanmar, Laos, Belarus, Moldova, Lebanon, Iraq, Bolivia, Ethiopia)
const COUNTRIES = [
  // Domestic
  { code: 'US', name: 'United States' },
  
  // Asia Pacific - PayPal supported
  { code: 'KR', name: 'South Korea', region: 'asia' },
  { code: 'JP', name: 'Japan', region: 'asia' },
  { code: 'CN', name: 'China', region: 'asia' },
  { code: 'TW', name: 'Taiwan', region: 'asia' },
  { code: 'HK', name: 'Hong Kong', region: 'asia' },
  { code: 'SG', name: 'Singapore', region: 'asia' },
  { code: 'PH', name: 'Philippines', region: 'asia' },
  { code: 'TH', name: 'Thailand', region: 'asia' },
  { code: 'MY', name: 'Malaysia', region: 'asia' },
  { code: 'ID', name: 'Indonesia', region: 'asia' },
  { code: 'VN', name: 'Vietnam', region: 'asia' },
  { code: 'IN', name: 'India', region: 'asia' },
  { code: 'LK', name: 'Sri Lanka', region: 'asia' },
  { code: 'NP', name: 'Nepal', region: 'asia' },
  { code: 'KH', name: 'Cambodia', region: 'asia' },
  { code: 'MN', name: 'Mongolia', region: 'asia' },
  { code: 'BN', name: 'Brunei', region: 'asia' },
  { code: 'MV', name: 'Maldives', region: 'asia' },
  
  // Oceania
  { code: 'AU', name: 'Australia', region: 'other' },
  { code: 'NZ', name: 'New Zealand', region: 'other' },
  { code: 'FJ', name: 'Fiji', region: 'other' },
  { code: 'PG', name: 'Papua New Guinea', region: 'other' },
  { code: 'GU', name: 'Guam', region: 'other' },
  
  // Europe - Western (Full PayPal support)
  { code: 'GB', name: 'United Kingdom', region: 'europe' },
  { code: 'DE', name: 'Germany', region: 'europe' },
  { code: 'FR', name: 'France', region: 'europe' },
  { code: 'IT', name: 'Italy', region: 'europe' },
  { code: 'ES', name: 'Spain', region: 'europe' },
  { code: 'NL', name: 'Netherlands', region: 'europe' },
  { code: 'BE', name: 'Belgium', region: 'europe' },
  { code: 'AT', name: 'Austria', region: 'europe' },
  { code: 'CH', name: 'Switzerland', region: 'europe' },
  { code: 'PT', name: 'Portugal', region: 'europe' },
  { code: 'IE', name: 'Ireland', region: 'europe' },
  { code: 'LU', name: 'Luxembourg', region: 'europe' },
  { code: 'MC', name: 'Monaco', region: 'europe' },
  
  // Europe - Nordic (Full PayPal support)
  { code: 'SE', name: 'Sweden', region: 'europe' },
  { code: 'NO', name: 'Norway', region: 'europe' },
  { code: 'DK', name: 'Denmark', region: 'europe' },
  { code: 'FI', name: 'Finland', region: 'europe' },
  { code: 'IS', name: 'Iceland', region: 'europe' },
  
  // Europe - Central/Eastern (PayPal supported)
  { code: 'PL', name: 'Poland', region: 'europe' },
  { code: 'CZ', name: 'Czech Republic', region: 'europe' },
  { code: 'SK', name: 'Slovakia', region: 'europe' },
  { code: 'HU', name: 'Hungary', region: 'europe' },
  { code: 'RO', name: 'Romania', region: 'europe' },
  { code: 'BG', name: 'Bulgaria', region: 'europe' },
  { code: 'HR', name: 'Croatia', region: 'europe' },
  { code: 'SI', name: 'Slovenia', region: 'europe' },
  { code: 'RS', name: 'Serbia', region: 'europe' },
  { code: 'BA', name: 'Bosnia and Herzegovina', region: 'europe' },
  { code: 'MK', name: 'North Macedonia', region: 'europe' },
  { code: 'AL', name: 'Albania', region: 'europe' },
  
  // Europe - Baltic (Full PayPal support)
  { code: 'LT', name: 'Lithuania', region: 'europe' },
  { code: 'LV', name: 'Latvia', region: 'europe' },
  { code: 'EE', name: 'Estonia', region: 'europe' },
  
  // Europe - Southern
  { code: 'GR', name: 'Greece', region: 'europe' },
  { code: 'CY', name: 'Cyprus', region: 'europe' },
  { code: 'MT', name: 'Malta', region: 'europe' },
  
  // Europe - Other (PayPal supported)
  { code: 'UA', name: 'Ukraine (excl. occupied regions)', region: 'europe' },
  { code: 'GE', name: 'Georgia', region: 'europe' },
  { code: 'AM', name: 'Armenia', region: 'europe' },
  { code: 'AZ', name: 'Azerbaijan', region: 'europe' },
  { code: 'TR', name: 'Turkey', region: 'europe' },
  
  // Middle East (PayPal supported)
  { code: 'AE', name: 'United Arab Emirates', region: 'middleEast' },
  { code: 'SA', name: 'Saudi Arabia', region: 'middleEast' },
  { code: 'IL', name: 'Israel', region: 'middleEast' },
  { code: 'QA', name: 'Qatar', region: 'middleEast' },
  { code: 'KW', name: 'Kuwait', region: 'middleEast' },
  { code: 'BH', name: 'Bahrain', region: 'middleEast' },
  { code: 'OM', name: 'Oman', region: 'middleEast' },
  { code: 'JO', name: 'Jordan', region: 'middleEast' },
  { code: 'EG', name: 'Egypt', region: 'middleEast' },
  
  // Americas (PayPal supported)
  { code: 'CA', name: 'Canada', region: 'other' },
  { code: 'MX', name: 'Mexico', region: 'other' },
  { code: 'BR', name: 'Brazil', region: 'other' },
  { code: 'AR', name: 'Argentina', region: 'other' },
  { code: 'CL', name: 'Chile', region: 'other' },
  { code: 'CO', name: 'Colombia', region: 'other' },
  { code: 'PE', name: 'Peru', region: 'other' },
  { code: 'EC', name: 'Ecuador', region: 'other' },
  { code: 'UY', name: 'Uruguay', region: 'other' },
  { code: 'CR', name: 'Costa Rica', region: 'other' },
  { code: 'PA', name: 'Panama', region: 'other' },
  { code: 'GT', name: 'Guatemala', region: 'other' },
  { code: 'DO', name: 'Dominican Republic', region: 'other' },
  { code: 'PR', name: 'Puerto Rico', region: 'other' },
  { code: 'JM', name: 'Jamaica', region: 'other' },
  { code: 'TT', name: 'Trinidad and Tobago', region: 'other' },
  { code: 'BS', name: 'Bahamas', region: 'other' },
  
  // Africa (PayPal supported)
  { code: 'ZA', name: 'South Africa', region: 'other' },
  { code: 'NG', name: 'Nigeria', region: 'other' },
  { code: 'MA', name: 'Morocco', region: 'other' },
  { code: 'KE', name: 'Kenya', region: 'other' },
  { code: 'GH', name: 'Ghana', region: 'other' },
  { code: 'TN', name: 'Tunisia', region: 'other' },
  { code: 'DZ', name: 'Algeria', region: 'other' },
  { code: 'TZ', name: 'Tanzania', region: 'other' },
  { code: 'UG', name: 'Uganda', region: 'other' },
  { code: 'SN', name: 'Senegal', region: 'other' },
  { code: 'MU', name: 'Mauritius', region: 'other' },
];


// Password strength checker
const checkPasswordStrength = (password) => {
  const checks = {
    length: password.length >= 10,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~]/.test(password),
  };
  
  const passed = Object.values(checks).filter(Boolean).length;
  let strength = 'weak';
  let color = 'bg-red-500';
  
  if (passed >= 5) {
    strength = 'strong';
    color = 'bg-green-500';
  } else if (passed >= 3) {
    strength = 'medium';
    color = 'bg-yellow-500';
  }
  
  return { checks, passed, strength, color };
};

export default function Register() {
  const navigate = useNavigate();
  const { register, user } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    country: '',
    home_country: '',  // User's actual country (saved when they use forwarding)
    address: '',
    apt_unit: '',
    city: '',
    state: '',
    zip_code: '',
    website: '' // Honeypot field
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [showForwardingInfo, setShowForwardingInfo] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [acknowledgedForwarding, setAcknowledgedForwarding] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  // Password strength calculation
  const passwordStrength = useMemo(() => 
    checkPasswordStrength(formData.password), 
    [formData.password]
  );

  // Redirect if already logged in
  if (user) {
    navigate('/');
    return null;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAcknowledgeForwarding = () => {
    // Save current country as home_country before they enter US forwarding address
    setFormData(prev => ({
      ...prev,
      home_country: prev.country  // Preserve their actual home country
    }));
    setAcknowledgedForwarding(true);
    setShowForwardingInfo(false);
  };

  const handleCountrySelect = (countryCode) => {
    setShowCountryDropdown(false);
    
    // If user already acknowledged forwarding and tries to switch to US, warn them
    // but still allow it - their home_country is preserved regardless
    if (acknowledgedForwarding && countryCode === 'US' && formData.home_country) {
      toast.error('Your home country is still saved. Enter your US forwarding address below.');
    }
    
    setFormData(prev => ({ ...prev, country: countryCode }));
    
    if (countryCode && countryCode !== 'US') {
      const country = COUNTRIES.find(c => c.code === countryCode);
      if (country) {
        setSelectedRegion(country.region);
        setShowForwardingInfo(true);
        setAcknowledgedForwarding(false);
      }
    } else {
      setShowForwardingInfo(false);
      setSelectedRegion(null);
      // Don't clear acknowledgedForwarding or home_country - preserve them!
    }
  };

  const getSelectedCountryName = () => {
    if (!formData.country) return 'Select your country';
    const country = COUNTRIES.find(c => c.code === formData.country);
    return country ? country.name : 'Select your country';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot check
    if (formData.website) {
      toast.success('Account created! Check your email.');
      return;
    }
    
    if (!formData.email || !formData.password || !formData.full_name || !formData.country ||
        !formData.address || !formData.city || !formData.state || !formData.zip_code) {
      toast.error('Please fill in all fields');
      return;
    }

    // Check if international user acknowledged forwarding
    if (formData.country !== 'US' && !acknowledgedForwarding) {
      toast.error('Please select a country and acknowledge the forwarding service info');
      return;
    }

    // International users MUST have suite/unit number (forwarding service requirement)
    // Check home_country - if set, they're international even if country shows US
    if ((formData.home_country || formData.country !== 'US') && (!formData.apt_unit || !formData.apt_unit.trim())) {
      toast.error('International customers must enter their forwarding service suite/unit number');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('PASSWORDS DO NOT MATCH');
      return;
    }

    // Strong password validation
    if (passwordStrength.passed < 5) {
      toast.error('PASSWORD DOES NOT MEET ALL REQUIREMENTS');
      return;
    }

    try {
      setLoading(true);
      
      // Build registration data, including home_country for international users
      const registrationData = {
        ...formData,
        // If they're international (acknowledged forwarding), set the flag
        is_international: formData.country !== 'US' && acknowledgedForwarding
      };
      
      const response = await register(registrationData);
      
      if (response?.requires_verification) {
        setShowVerificationMessage(true);
        toast.success('Check your email to verify your account!');
      } else {
        toast.success('ACCOUNT CREATED - You can now login');
        navigate('/login');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Registration failed';
      // Check for specific error messages
      if (errorMsg.includes('not shipping to that location')) {
        toast.error('We are not shipping to that location at this time.');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  if (showVerificationMessage) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col">
        <header className="border-b border-zinc-800 bg-[#050505]">
          <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
            <Link to="/catalog" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
            </Link>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-md text-center">
            <div className="w-16 h-16 mx-auto mb-6 border-2 border-tiffany-500 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-tiffany-500" />
            </div>
            <h1 className="font-sans text-3xl text-white mb-4">CHECK YOUR EMAIL</h1>
            <p className="font-mono text-zinc-400 mb-6">
              We've sent a verification link to <span className="text-tiffany-500">{formData.email}</span>
            </p>
            <p className="font-mono text-sm text-zinc-500 mb-8">
              Click the link in the email to verify your account. The link expires in 24 hours.
            </p>
            <div className="space-y-4">
              <Link 
                to="/login"
                className="block w-full h-12 bg-tiffany-500 text-black hover:bg-tiffany-400 font-sans tracking-widest flex items-center justify-center"
              >
                GO TO LOGIN
              </Link>
              <p className="font-mono text-xs text-zinc-600">
                Didn't receive the email? Check your spam folder or contact support.
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/catalog" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
          </Link>
          <nav className="flex items-center gap-4">
            <Link 
              to="/catalog"
              className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors"
              data-testid="home-link"
            >
              <Home className="w-5 h-5" />
            </Link>
          </nav>
        </div>
      </header>

      {/* Register Form */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg">
          <div className="text-center mb-8">
            <h1 className="font-sans text-4xl text-white mb-2" data-testid="register-title">
              CREATE ACCOUNT
            </h1>
            <p className="font-mono text-xs text-tiffany-500 mt-2">
              SHIPPING WORLDWIDE VIA FORWARDING SERVICES
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Login Info */}
            <div className="space-y-4">
              <h2 className="font-sans text-lg text-zinc-400 border-b border-zinc-800 pb-2">
                LOGIN INFO
              </h2>
              <div>
                <label className="block font-mono text-sm text-zinc-500 mb-2">
                  EMAIL *
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className="w-full h-12 bg-zinc-900 border-zinc-800 text-white font-mono placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none"
                  required
                  data-testid="register-email"
                />
              </div>

              <div>
                <label className="block font-mono text-sm text-zinc-500 mb-2">
                  PASSWORD *
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Create a strong password"
                    className="w-full h-12 bg-zinc-900 border-zinc-800 text-white font-mono placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none pr-12"
                    required
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                
                {/* Password Strength Meter */}
                {formData.password && (
                  <div className="mt-3 space-y-2">
                    {/* Strength Bar */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1 bg-zinc-800 overflow-hidden">
                        <div 
                          className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                          style={{ width: `${(passwordStrength.passed / 5) * 100}%` }}
                        />
                      </div>
                      <span className={`font-mono text-[10px] uppercase ${
                        passwordStrength.strength === 'strong' ? 'text-green-500' :
                        passwordStrength.strength === 'medium' ? 'text-yellow-500' : 'text-red-500'
                      }`}>
                        {passwordStrength.strength}
                      </span>
                    </div>
                    
                    {/* Requirements Checklist */}
                    <div className="grid grid-cols-2 gap-1">
                      <div className={`flex items-center gap-1.5 font-mono text-[10px] ${passwordStrength.checks.length ? 'text-green-500' : 'text-zinc-600'}`}>
                        {passwordStrength.checks.length ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        10+ CHARACTERS
                      </div>
                      <div className={`flex items-center gap-1.5 font-mono text-[10px] ${passwordStrength.checks.uppercase ? 'text-green-500' : 'text-zinc-600'}`}>
                        {passwordStrength.checks.uppercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        UPPERCASE
                      </div>
                      <div className={`flex items-center gap-1.5 font-mono text-[10px] ${passwordStrength.checks.lowercase ? 'text-green-500' : 'text-zinc-600'}`}>
                        {passwordStrength.checks.lowercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        LOWERCASE
                      </div>
                      <div className={`flex items-center gap-1.5 font-mono text-[10px] ${passwordStrength.checks.number ? 'text-green-500' : 'text-zinc-600'}`}>
                        {passwordStrength.checks.number ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        NUMBER
                      </div>
                      <div className={`flex items-center gap-1.5 font-mono text-[10px] ${passwordStrength.checks.special ? 'text-green-500' : 'text-zinc-600'}`}>
                        {passwordStrength.checks.special ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        SPECIAL CHAR
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block font-mono text-sm text-zinc-500 mb-2">
                  CONFIRM PASSWORD *
                </label>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm password"
                  className="w-full h-12 bg-zinc-900 border-zinc-800 text-white font-mono placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none"
                  required
                  data-testid="register-confirm-password"
                />
              </div>
            </div>

            {/* Shipping Info */}
            <div className="space-y-4">
              <h2 className="font-sans text-lg text-zinc-400 border-b border-zinc-800 pb-2">
                SHIPPING INFO
              </h2>
              
              {/* Country Selection - Custom Dropdown */}
              <div className="relative">
                <label className="block font-mono text-sm text-zinc-500 mb-2">
                  <Globe className="w-4 h-4 inline mr-2" />
                  COUNTRY *
                </label>
                <button
                  type="button"
                  onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                  className="w-full h-12 bg-zinc-900 border border-zinc-800 text-white font-mono focus:border-tiffany-500 rounded-none px-3 text-left flex items-center justify-between"
                  data-testid="register-country"
                >
                  <span className={formData.country ? 'text-white' : 'text-zinc-600'}>
                    {getSelectedCountryName()}
                  </span>
                  <svg className={`w-4 h-4 text-zinc-500 transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {/* Country Dropdown List */}
                {showCountryDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-zinc-900 border border-zinc-700 max-h-64 overflow-y-auto">
                    {COUNTRIES.map(country => (
                      <button
                        key={country.code}
                        type="button"
                        onClick={() => handleCountrySelect(country.code)}
                        className={`w-full px-3 py-3 text-left font-mono text-sm hover:bg-zinc-800 border-b border-zinc-800 last:border-b-0 ${
                          formData.country === country.code 
                            ? 'bg-tiffany-500/20 text-tiffany-500' 
                            : 'text-white'
                        }`}
                      >
                        {country.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* International Customer Notice - show if they have a home_country saved */}
              {formData.home_country && (
                <div className="bg-tiffany-500/10 border border-tiffany-500/30 p-4">
                  <p className="font-mono text-sm text-tiffany-500">
                    INTERNATIONAL CUSTOMER - Enter your US forwarding service address below
                  </p>
                  <p className="font-mono text-[10px] text-zinc-500 mt-2">
                    Your home country ({COUNTRIES.find(c => c.code === formData.home_country)?.name || formData.home_country}) is saved.
                  </p>
                </div>
              )}

              {/* Forwarding Service Modal */}
              {showForwardingInfo && selectedRegion && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                  <div className="bg-zinc-900 border border-zinc-700 max-w-lg w-full max-h-[90vh] overflow-y-auto">
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <Globe className="w-6 h-6 text-tiffany-500" />
                        <h3 className="font-sans text-xl text-white">INTERNATIONAL SHIPPING</h3>
                      </div>
                      
                      <p className="font-mono text-sm text-zinc-400 mb-6">
                        We ship to US addresses only. International customers can use a package forwarding service to receive BLK MRKT orders worldwide.
                      </p>

                      <div className="space-y-3 mb-5">
                        <h4 className="font-mono text-xs text-zinc-400 uppercase tracking-wider">
                          Recommended for {FORWARDING_SERVICES[selectedRegion]?.label || 'Your Region'}:
                        </h4>
                        
                        {FORWARDING_SERVICES[selectedRegion]?.services.map((service, idx) => (
                          <a
                            key={idx}
                            href={service.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-between bg-zinc-800 border border-zinc-700 px-4 py-3 hover:border-tiffany-500 transition-colors"
                          >
                            <div>
                              <span className="font-sans text-sm text-white">{service.name}</span>
                              <p className="font-mono text-xs text-zinc-500">{service.note}</p>
                            </div>
                            <ExternalLink className="w-4 h-4 text-zinc-500 shrink-0" />
                          </a>
                        ))}
                      </div>

                      <div className="bg-zinc-800 p-4 mb-5">
                        <h4 className="font-mono text-xs text-zinc-300 mb-2">HOW IT WORKS:</h4>
                        <ol className="font-mono text-xs text-zinc-500 space-y-1.5">
                          <li>1. Sign up with a forwarding service above</li>
                          <li>2. Get your US warehouse address</li>
                          <li>3. Use that address when creating your BLK MRKT account</li>
                          <li>4. Your orders ship to the warehouse, then to you internationally</li>
                        </ol>
                      </div>

                      <div className="flex gap-3">
                        <Button
                          type="button"
                          onClick={() => {
                            setShowForwardingInfo(false);
                            setFormData(prev => ({ ...prev, country: '' }));
                          }}
                          variant="outline"
                          className="flex-1 h-12 border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-none font-mono"
                        >
                          CANCEL
                        </Button>
                        <Button
                          type="button"
                          onClick={handleAcknowledgeForwarding}
                          className="flex-1 h-12 bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none font-sans tracking-wider"
                        >
                          I HAVE A US ADDRESS
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block font-mono text-sm text-zinc-500 mb-2">
                  FULL NAME *
                </label>
                <Input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  autoComplete="name"
                  className="w-full h-12 bg-zinc-900 border-zinc-800 text-white font-mono placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none"
                  required
                  data-testid="register-fullname"
                />
              </div>

              <div>
                <label className="block font-mono text-sm text-zinc-500 mb-2">
                  STREET ADDRESS *
                </label>
                <Input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="123 Main St"
                  autoComplete="street-address"
                  className="w-full h-12 bg-zinc-900 border-zinc-800 text-white font-mono placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none"
                  required
                  data-testid="register-address"
                />
              </div>

              {/* APT/UNIT/SUITE - Required for international/forwarding users */}
              <div>
                <label className="block font-mono text-sm text-zinc-500 mb-2">
                  APT / UNIT / SUITE {(formData.home_country || (formData.country && formData.country !== 'US')) && <span className="text-purple-400">* (YOUR FORWARDER ID)</span>}
                </label>
                <Input
                  type="text"
                  name="apt_unit"
                  value={formData.apt_unit}
                  onChange={handleChange}
                  placeholder={(formData.home_country || (formData.country && formData.country !== 'US')) ? "e.g. Suite KT-12131" : "Apt 2B (optional)"}
                  autoComplete="address-line2"
                  className="w-full h-12 bg-zinc-900 border-zinc-800 text-white font-mono placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none"
                  data-testid="register-apt-unit"
                  required={!!(formData.home_country || (formData.country && formData.country !== 'US'))}
                />
              </div>

              {/* City on its own row on mobile, State and ZIP together */}
              <div>
                <label className="block font-mono text-sm text-zinc-500 mb-2">
                  CITY *
                </label>
                <Input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="City"
                  autoComplete="address-level2"
                  className="w-full h-12 bg-zinc-900 border-zinc-800 text-white font-mono placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none"
                  required
                  data-testid="register-city"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-mono text-sm text-zinc-500 mb-2">
                    STATE *
                  </label>
                  <Select
                    value={formData.state}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, state: value }))}
                  >
                    <SelectTrigger 
                      className="w-full h-12 bg-zinc-900 border-zinc-800 text-white font-mono rounded-none"
                      data-testid="register-state"
                    >
                      <SelectValue placeholder="SELECT STATE" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700 max-h-60">
                      {US_STATES.map(state => (
                        <SelectItem 
                          key={state.code} 
                          value={state.code}
                          className="text-white font-mono hover:bg-zinc-800 uppercase"
                        >
                          {state.code} - {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block font-mono text-sm text-zinc-500 mb-2">
                    ZIP *
                  </label>
                  <Input
                    type="text"
                    name="zip_code"
                    value={formData.zip_code}
                    onChange={(e) => {
                      // Restrict to 5 digits only
                      const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                      setFormData(prev => ({ ...prev, zip_code: value }));
                    }}
                    placeholder="12345"
                    maxLength={5}
                    autoComplete="postal-code"
                    className="w-full h-12 bg-zinc-900 border-zinc-800 text-white font-mono placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none"
                    required
                    data-testid="register-zip"
                  />
                </div>
              </div>
            </div>

            {/* Honeypot field */}
            <div className="absolute -left-[9999px]" aria-hidden="true">
              <Input
                type="text"
                name="website"
                value={formData.website}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-tiffany-500 text-black hover:bg-tiffany-400 font-sans text-lg tracking-widest rounded-none disabled:opacity-50"
              data-testid="register-submit"
            >
              {loading ? 'CREATING...' : 'CREATE ACCOUNT'}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="font-mono text-sm text-zinc-500">
              ALREADY HAVE AN ACCOUNT?{' '}
              <Link 
                to="/login" 
                className="text-tiffany-500 hover:underline"
                data-testid="login-link"
              >
                LOGIN
              </Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
